import { AnimationProvider, Button, Icon, IconsName, Text } from '@/components';
import APP_CONFIG from '@/config/app';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
const { MAIN_ROUNDEDNESS } = APP_CONFIG.STYLES;
const roundednessConfig = {
  roundedness: `rounded${MAIN_ROUNDEDNESS}`,
};
const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;
interface ToastProps {
  isOpen: boolean;
  onClose: () => void;
  icon?: IconsName;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  isBackgroundBlurred?: boolean;
  slideFrom?: 'left' | 'right' | 'top' | 'bottom';
  autoClose?: boolean;
  className?: string;
}
const Toast: FC<ToastProps> = ({
  isOpen,
  onClose,
  icon = 'check',
  title,
  description,
  isBackgroundBlurred = true,
  slideFrom = 'right',
  autoClose = true,
  className = '',
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setInternalIsOpen(true);
      if (autoClose) {
        const timer = setTimeout(() => {
          setInternalIsOpen(false);
          setTimeout(onClose, APP_CONFIG.ANIMATION_SETTINGS.COMMON.duration);
        }, APP_CONFIG.ANIMATION_SETTINGS.TOAST.OPEN_DURATION);
        return () => clearTimeout(timer);
      }
    } else {
      setInternalIsOpen(false);
    }
  }, [isOpen, autoClose, onClose]);
  const handleAnimationComplete = () => {
    if (!internalIsOpen) {
      setShouldRender(false);
    }
  };
  const handleOnClose = () => {
    setInternalIsOpen(false);
    setTimeout(onClose, APP_CONFIG.ANIMATION_SETTINGS.COMMON.duration);
  };
  const slideDirection = slideFrom.toUpperCase() as
    | 'LEFT'
    | 'RIGHT'
    | 'TOP'
    | 'BOTTOM';
  const slideAnimationConfig =
    APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM[
      slideDirection as keyof typeof APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM
    ];
  const variants = {
    initial: slideAnimationConfig.initial,
    animate: slideAnimationConfig.open,
    exit: slideAnimationConfig.closed,
  };
  return createPortal(
    <AnimationProvider>
      <AnimatePresence>
        {shouldRender && (
          <motion.div
            key="toast"
            initial="initial"
            animate={internalIsOpen ? 'animate' : 'exit'}
            exit="exit"
            variants={variants}
            transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
            onAnimationComplete={handleAnimationComplete}
            className={`fixed p-md top-md right-md w-[40%] max-w-lg min-w-[300px] z-50 flex flex-col bg-primary/14 border border-solid border-primary/10 ${
              roundednessConfig.roundedness
            }  ${
              isBackgroundBlurred ? `backdrop-blur${MAIN_BACKDROP_BLUR}` : ''
            } ${className}`}
          >
            <div className="flex items-start justify-between">
              {icon && (
                <div className="flex-shrink-0 flex items-start h-full mr-sm">
                  <Icon name={icon} className="h-full" />
                </div>
              )}
              <div className="flex flex-col flex-grow">
                {typeof title === 'string' ? (
                  <Text value={title} color="primary" size="h4" />
                ) : (
                  title
                )}
                {description && (
                  <div className="mt-2">
                    {typeof description === 'string' ? (
                      <Text value={description} color="secondary" />
                    ) : (
                      description
                    )}
                  </div>
                )}
              </div>
              {!autoClose && (
                <div
                  className={`flex-shrink-0 flex items-start h-full ml-sm ${
                    autoClose ? 'invisible' : ''
                  }`}
                >
                  <Button
                    onClick={handleOnClose}
                    className="cursor-pointer bg-none border-none p-0 m-0 h-full"
                    intent="ghost"
                    icon="close"
                    iconStyles={{ intent: 'accent' }}
                  />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimationProvider>,
    document.body,
  );
};
export default Toast;
